import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { get, isNull } from 'lodash-es';
import Col from 'reactstrap/lib/Col';
import { getPlural } from 'client/utils/plural';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import {
  IncentivesElectricEntities,
  IncentivesElectricModel,
  IncentivesElectricPaths,
} from 'client/data/models/incentives-electric';
import {
  CARDS_LIST_CREATIVE_ID,
  DISPLAY_MODE,
  ELECTRIC_CAR_INCENTIVES_CARDS_ID,
  IncentivesConfigPropType,
} from 'site-modules/shared/constants/incentives/electric-car-incentives';
import { IncentivesModal } from 'site-modules/shared/components/incentives/incentives-modal/incentives-modal';
import { CardsList } from 'site-modules/shared/components/incentives/cards-list/cards-list';
import { IncentiveCard } from 'site-modules/shared/components/incentives/incentive-card/incentive-card';
import { connect } from 'react-redux';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VisitorModel } from 'client/data/models/visitor';
import { Carousel } from 'site-modules/shared/components/carousel/carousel';
import { EvIncentivesZip } from 'site-modules/editorial/components/type-buying-guide/ev-incentives-zip/ev-incentives-zip';
import { IncentivesModalData } from 'site-modules/shared/components/incentives/incentives-modal-data/incentives-modal-data';
import { usePrevious } from 'site-modules/shared/hooks/use-previous';

import './ev-incentives-cards.scss';

export function EvIncentivesCardsUI({ incentivesData = null, incentivesConfig, isMobile = false, sessionId = null }) {
  const { makeSlug, modelSlug } = incentivesConfig;
  const [cardDetailsId, setCardDetailsId] = useState(null);
  const isPageLoadTracking = useRef(true);
  const [isLoading, setIsLoading] = useState(isNull(incentivesData));
  const [incentivesState, setIncentivesState] = useState(incentivesData);
  const prevIncentivesData = usePrevious(incentivesData);

  if (prevIncentivesData !== incentivesData) {
    if (isNull(incentivesData)) {
      setIsLoading(true);
    } else {
      setIncentivesState(incentivesData);
      setIsLoading(false);
    }
  }

  const incentivesOrdered = get(incentivesState, 'incentivesOrdered', []);
  const incentivesCount = get(incentivesState, 'incentivesCount', 0);

  const toggleIncentivesModal = useCallback(event => {
    setCardDetailsId(get(event, 'currentTarget.dataset.modalid'));
  }, []);

  let modalData = incentivesOrdered.find(({ id }) => id === cardDetailsId);
  modalData = modalData ? { ...modalData, sessionId } : modalData;

  useEffect(() => {
    if (isLoading) {
      return;
    }
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_VIEW_CONTENT,
        subaction_name: 'display_incentives',
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        action_cause: isPageLoadTracking.current
          ? TrackingConstant.PAGE_LOAD_CAUSE
          : TrackingConstant.ACTION_CAUSE_USER_INPUT,
        creative_id: CARDS_LIST_CREATIVE_ID[DISPLAY_MODE.INCENTIVES],
        value: incentivesOrdered.length,
      },
    });
    isPageLoadTracking.current = false;
  }, [isLoading, incentivesOrdered.length]);

  if (!isLoading && !incentivesOrdered.length) return null;

  return (
    <div className="ev-incentives-cards mt-1 mt-md-2">
      <EvIncentivesZip />
      <div id={ELECTRIC_CAR_INCENTIVES_CARDS_ID} role="region" aria-label="Listings">
        <span className="visually-hidden" aria-live="polite" aria-atomic>
          {`${incentivesCount}`} {getPlural('result', incentivesCount)}
        </span>

        {isMobile ? (
          <Carousel
            swipe
            infinite={false}
            dots
            arrows={false}
            dotsClass="slick-dots gray-custom d-flex justify-content-center align-items-center"
            className="mb-1"
            ariaLive="off"
          >
            {incentivesOrdered.map(({ id, subtype, rebateAmount, endDate, restrictions, programType, grantor }) => (
              <IncentiveCard
                key={id}
                id={id}
                makeSlug={makeSlug}
                modelSlug={modelSlug}
                subtype={subtype}
                rebateAmount={rebateAmount}
                programType={programType}
                endDate={endDate}
                grantor={grantor}
                restrictions={restrictions}
                openIncentivesModal={toggleIncentivesModal}
                showPlaceholder={isLoading}
              />
            ))}
          </Carousel>
        ) : (
          <CardsList
            activeDisplayMode={DISPLAY_MODE.INCENTIVES}
            displayMode={DISPLAY_MODE.INCENTIVES}
            dataTrackingParent={CARDS_LIST_CREATIVE_ID[DISPLAY_MODE.INCENTIVES]}
          >
            {incentivesOrdered.map(({ id, subtype, rebateAmount, endDate, restrictions, programType, grantor }) => (
              <Col xs={12} md={4} lg={3} className="mb-1" tag="li" key={id}>
                <IncentiveCard
                  id={id}
                  makeSlug={makeSlug}
                  modelSlug={modelSlug}
                  subtype={subtype}
                  rebateAmount={rebateAmount}
                  programType={programType}
                  endDate={endDate}
                  grantor={grantor}
                  restrictions={restrictions}
                  openIncentivesModal={toggleIncentivesModal}
                  showPlaceholder={isLoading}
                />
              </Col>
            ))}
          </CardsList>
        )}
        <IncentivesModal toggleModal={toggleIncentivesModal} modalData={modalData}>
          <IncentivesModalData modalData={modalData} />
        </IncentivesModal>
      </div>
    </div>
  );
}

EvIncentivesCardsUI.propTypes = {
  incentivesData: IncentivesElectricEntities.IncentivesData,
  incentivesConfig: IncentivesConfigPropType.isRequired,
  sessionId: PropTypes.string,
  isMobile: PropTypes.bool,
};

const mapStateToProps = state => ({
  isMobile: get(state, 'mobile'),
  location: get(state, 'pageContext.location'),
});

export const EvIncentivesCards = connect(mapStateToProps)(
  connectToModel(EvIncentivesCardsUI, {
    sessionId: bindToPath('session', VisitorModel),
    incentivesData: bindToPath(
      ({ incentivesConfig }) => IncentivesElectricPaths.getIncentivesGuidePath(incentivesConfig),
      IncentivesElectricModel,
      null,
      false
    ),
  })
);
